import React from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { FaChargingStation } from 'react-icons/fa';
import {
  IoLocationSharp, IoSettingsSharp, IoTimeOutline, IoShieldCheckmarkSharp,
  IoCallSharp, IoMailSharp, IoGlobeOutline, IoBusinessSharp, IoPricetagsSharp,
  IoEyeSharp, IoCheckmarkCircle, IoCloseCircle,
  IoStarSharp, IoStarHalfSharp, IoStarOutline, IoImagesSharp, IoPeopleSharp,
  IoHomeSharp, IoConstructSharp, IoWalletSharp, IoKeySharp, IoBookmarkSharp
} from 'react-icons/io5';
import { HiIdentification, HiLightningBolt } from 'react-icons/hi';
import { TbBuildingSkyscraper } from 'react-icons/tb';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { MAP_TILE_CONFIG } from 'config/mapTileConfig';
import PropTypes from 'prop-types';

// Helper: boolean badge
const BoolBadge = ({ value, trueText = 'Yes', falseText = 'No' }) => (
  <span className={`sim-badge ${value ? 'sim-badge--green' : 'sim-badge--red'}`}>
    {value ? <IoCheckmarkCircle /> : <IoCloseCircle />}
    {value ? trueText : falseText}
  </span>
);

BoolBadge.propTypes = {
  value: PropTypes.bool,
  trueText: PropTypes.string,
  falseText: PropTypes.string,
};

// Helper: key-value row
const KVRow = ({ icon, iconBg, label, value, full }) => (
  <div className={`sim-kv-row ${full ? 'sim-kv-row--full' : ''}`}>
    {icon && (
      <div className="sim-kv__icon" style={{ background: iconBg || 'rgba(60,124,221,0.1)' }}>
        {icon}
      </div>
    )}
    <div className="sim-kv__content">
      <span className="sim-kv__label">{label}</span>
      <span className="sim-kv__value">{value ?? 'N/A'}</span>
    </div>
  </div>
);

KVRow.propTypes = {
  icon: PropTypes.node,
  iconBg: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.node,
  full: PropTypes.bool,
};

// Helper: star rating renderer
const RenderStars = ({ rating = 0 }) => {
  const stars = [];
  const r = parseFloat(rating) || 0;
  for (let i = 1; i <= 5; i++) {
    if (i <= Math.floor(r)) stars.push(<IoStarSharp key={i} />);
    else if (i - 0.5 <= r) stars.push(<IoStarHalfSharp key={i} />);
    else stars.push(<IoStarOutline key={i} style={{ opacity: 0.3 }} />);
  }
  return <div className="sim-rating-stars">{stars}</div>;
};

RenderStars.propTypes = {
  rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

// Helper: safe value renderer
const renderSafeValue = (val) => {
  if (val === null || val === undefined) return 'N/A';
  if (typeof val === 'object' && !React.isValidElement(val)) {
    return val.name || val.first_name || JSON.stringify(val);
  }
  return val;
};

const StationInfoModal = ({ show, onClose, data, stationIcon }) => {
  const [zoomedImage, setZoomedImage] = React.useState(null);
  const [copySuccess, setCopySuccess] = React.useState(false);

  if (!data) return null;

  const d = data; // shorthand

  // Safe extractors
  const getTenantDisplay = () => {
    const t = _.get(d, 'tenant');
    if (!t) return 'N/A';
    if (typeof t === 'string') return t;
    return t.name || t.first_name || 'N/A';
  };

  const getContactDisplay = () => {
    const cp = _.get(d, 'contact_person');
    const cn = _.get(d, 'contact_name');
    const target = cp || cn;
    if (!target) return 'N/A';
    if (typeof target === 'string') return target;
    return target.name || (target.first_name ? `${target.first_name} ${target.last_name || ''}` : JSON.stringify(target));
  };

  return (
    <Modal show={show} size="lg" centered onHide={onClose} className="station-info-modal-overlay">
      <div className="station-info-modal">
        {/* ── ZOOMED VIEW OVERLAY ── */}
        {zoomedImage && (
          <div className="sim-zoom-overlay" onClick={() => setZoomedImage(null)}>
            <div className="zoom-close"><MdClose /></div>
            <img src={zoomedImage} alt="Zoomed" onClick={(e) => e.stopPropagation()} />
          </div>
        )}

        {/* ── HEADER ── */}
        <div className="sim-header">
          <div className="sim-header__left">
            <div className="sim-header__icon"><FaChargingStation /></div>
            <div className="sim-header__title">
              <h3>{_.get(d, 'name', 'Station Details')}</h3>
              <div className="sim-header__id" style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <span>ID: {_.get(d, 'station_id', '—')} &nbsp;|&nbsp; {_.get(d, 'countryCode', '')} &nbsp;|&nbsp; Tenant: {getTenantDisplay()}</span>
                {_.get(d, 'avg_rating') && <RenderStars rating={_.get(d, 'avg_rating', 0)} />}
              </div>
            </div>
          </div>
          <button className="sim-header__close" onClick={onClose}><MdClose /></button>
        </div>

        {/* ── STATS CARDS ── */}
        <div className="sim-stats-row">
          <div className="sim-stat-card">
            <div className="sim-stat__icon" style={{ background: 'rgba(20,172,127,0.12)' }}>
              <HiLightningBolt style={{ color: '#14ac7f' }} />
            </div>
            <div className="sim-stat__value">{((_.get(d, 'energy_consumed', 0)) / 1000).toFixed(1)}</div>
            <div className="sim-stat__label">kWh Consumed</div>
          </div>
          <div className="sim-stat-card">
            <div className="sim-stat__icon" style={{ background: 'rgba(60,124,221,0.12)' }}>
              <FaChargingStation style={{ color: '#3c7cdd' }} />
            </div>
            <div className="sim-stat__value">{_.get(d, 'total_chargers', 0)}</div>
            <div className="sim-stat__label">Total Chargers</div>
          </div>

          <div className="sim-stat-card">
            <div className="sim-stat__icon" style={{ background: 'rgba(190,33,11,0.12)' }}>
              <IoStarSharp style={{ color: '#be210b' }} />
            </div>
            <div className="sim-stat__value">{_.get(d, 'avg_rating', 0)} <span style={{ fontSize: '0.65rem', fontWeight: 400, color: '#8d949b' }}>({_.get(d, 'rating_count', 0)})</span></div>
            <div className="sim-stat__label">Avg Rating</div>
          </div>
        </div>

        <div className="sim-body">
          {/* ── WORKING HOURS (Moved to top as requested) ── */}
          <div className="sim-section">
            <div className="sim-section__title"><IoTimeOutline /> Working Hours</div>
            <div className="sim-kv-grid" style={{ marginBottom: 8 }}>
              <KVRow icon={<IoTimeOutline style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Timing Type" value={_.get(d, 'timing_type', 'N/A')} />
            </div>
            {!_.isEmpty(_.get(d, 'working_hours')) && (
              <div className="sim-hours-grid">
                {_.get(d, 'working_hours', []).map((wh, i) => (
                  <div className="sim-hour-item" key={i}>
                    <span className="sim-hour__day">{_.get(wh, 'day', `Day ${i + 1}`)}</span>
                    <span className="sim-hour__time">
                      {moment(_.get(wh, 'from', ''), 'hh:mm A').format('LT')} - {moment(_.get(wh, 'to', ''), 'hh:mm A').format('LT')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── PHOTOS ── */}
          {!_.isEmpty(_.get(d, 'photos', [])) && (
            <div className="sim-section">
              <div className="sim-section__title"><IoImagesSharp /> Photos (Click to Zoom)</div>
              <div className="sim-photos-strip">
                {_.get(d, 'photos', []).map((photo, i) => (
                  <img 
                    key={i} 
                    src={photo} 
                    alt={`station-photo-${i}`} 
                    onClick={() => setZoomedImage(photo)}
                    style={{ cursor: 'zoom-in' }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── STATUS & OPERATIONS ── */}
          <div className="sim-section">
            <div className="sim-section__title"><IoShieldCheckmarkSharp /> Status & Operations</div>
            <div className="sim-kv-grid">
              <KVRow icon={<IoShieldCheckmarkSharp style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="Status" value={<span className={`sim-badge ${d.status === 'Available' ? 'sim-badge--green' : d.status === 'Unavailable' ? 'sim-badge--grey' : 'sim-badge--orange'}`}>{_.get(d, 'status', 'N/A')}</span>} />
              <KVRow icon={<IoConstructSharp style={{ color: '#FF8B00' }} />} iconBg="rgba(255,139,0,0.1)" label="Operational Status" value={<span className={`sim-badge ${d.operational_status === 'Active' ? 'sim-badge--green' : d.operational_status === 'UnderMaintenance' ? 'sim-badge--grey' : 'sim-badge--orange'}`}>{_.get(d, 'operational_status', 'N/A')}</span>} />
              <KVRow icon={<IoKeySharp style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Access Type" value={_.get(d, 'access_type', 'N/A')} />
              <KVRow icon={<IoConstructSharp style={{ color: '#8d949b' }} />} iconBg="rgba(141,148,155,0.1)" label="Maintenance Type" value={_.get(d, 'maintenance_type', 'N/A')} />
              <KVRow icon={<IoBusinessSharp style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Operation Type" value={_.get(d, 'operation_type', 'N/A')} />
              <KVRow icon={<IoBusinessSharp style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="Ownership Type" value={renderSafeValue(_.get(d, 'ownership_type'))} />
            </div>
          </div>

          {/* ── LOCATION ── */}
          <div className="sim-section">
            <div className="sim-section__title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><IoLocationSharp /> Location & Address</div>
              {_.get(d, 'location.lat') && (
                <button 
                  className="sim-action-btn"
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${d.location.lat},${d.location.lng}`;
                    navigator.clipboard.writeText(url);
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                  }}
                >
                  {copySuccess ? <><IoCheckmarkCircle /> Copied</> : <><IoGlobeOutline /> Share Location</>}
                </button>
              )}
            </div>
            <div className="sim-kv-grid">
              <KVRow icon={<IoGlobeOutline style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Country" value={_.get(d, 'country', 'N/A')} />
              <KVRow icon={<TbBuildingSkyscraper style={{ color: '#fbaa26' }} />} iconBg="rgba(251,170,38,0.1)" label="State" value={_.get(d, 'state', 'N/A')} />
              <KVRow icon={<IoBusinessSharp style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="City" value={_.get(d, 'city', 'N/A')} />
              <KVRow icon={<IoLocationSharp style={{ color: '#be210b' }} />} iconBg="rgba(190,33,11,0.1)" label="Area" value={_.get(d, 'area', 'N/A')} />
              <KVRow icon={<IoGlobeOutline style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Region" value={_.get(d, 'region.name') || _.get(d, 'region') || 'N/A'} />
              <KVRow icon={<TbBuildingSkyscraper style={{ color: '#fbaa26' }} />} iconBg="rgba(251,170,38,0.1)" label="Zone" value={_.get(d, 'zone.name') || _.get(d, 'zone') || 'N/A'} />
              <KVRow icon={<IoPeopleSharp style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="Regional Manager" value={_.get(d, 'regional_manager', 'N/A')} />
              <KVRow icon={<IoMailSharp style={{ color: '#8d949b' }} />} iconBg="rgba(141,148,155,0.1)" label="Pincode" value={_.get(d, 'pincode', 'N/A')} />
              <KVRow icon={<TbBuildingSkyscraper style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="City Tier" value={_.get(d, 'city_tier', 'N/A')} />
              <KVRow full icon={<IoLocationSharp style={{ color: '#3c7cdd' }} />} iconBg="rgba(60,124,221,0.1)" label="Address" value={_.get(d, 'address', 'N/A')} />
              <KVRow icon={<IoGlobeOutline style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="Latitude" value={_.get(d, 'location.lat', 'N/A')} />
              <KVRow icon={<IoGlobeOutline style={{ color: '#3c7cdd' }} />} iconBg="rgba(60,124,221,0.1)" label="Longitude" value={_.get(d, 'location.lng', 'N/A')} />
            </div>
            {_.get(d, 'location.lat') && _.get(d, 'location.lng') && (
              <div className="sim-map-block">
                <MapContainer center={[_.get(d, 'location.lat'), _.get(d, 'location.lng')]} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                  <TileLayer url={MAP_TILE_CONFIG.URL} attribution={MAP_TILE_CONFIG.ATTRIBUTION} maxZoom={MAP_TILE_CONFIG.MAX_ZOOM} />
                  {stationIcon && <Marker position={[_.get(d, 'location.lat'), _.get(d, 'location.lng')]} icon={stationIcon} />}
                </MapContainer>
              </div>
            )}
          </div>

          {/* ── CONTACT ── */}
          <div className="sim-section">
            <div className="sim-section__title"><IoCallSharp /> Contact Information</div>
            <div className="sim-kv-grid">
              <KVRow icon={<IoPeopleSharp style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Contact Person" value={getContactDisplay()} />
              <KVRow icon={<IoMailSharp style={{ color: '#fbaa26' }} />} iconBg="rgba(251,170,38,0.1)" label="Contact Email" value={_.get(d, 'contact_email', 'N/A')} />
              <KVRow full icon={<IoLocationSharp style={{ color: '#8d949b' }} />} iconBg="rgba(141,148,155,0.1)" label="Contact Address" value={_.get(d, 'contact_address', 'N/A')} />
            </div>
          </div>

          {/* ── VISIBILITY & APP SETTINGS ── */}
          <div className="sim-section">
            <div className="sim-section__title"><IoEyeSharp /> Visibility & App Settings</div>
            <div className="sim-kv-grid">
              <KVRow icon={<IoEyeSharp style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="Available to Users" value={<BoolBadge value={_.get(d, 'availableToUsers')} />} />
              <KVRow icon={<IoEyeSharp style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Show on Mobile App" value={<BoolBadge value={_.get(d, 'show_on_mobile_app')} />} />
              <KVRow icon={<IoEyeSharp style={{ color: '#fbaa26' }} />} iconBg="rgba(251,170,38,0.1)" label="Show on CZ App" value={<BoolBadge value={_.get(d, 'show_on_cz_app')} />} />
              <KVRow icon={<IoGlobeOutline style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="Show to OCPI Partners" value={<BoolBadge value={_.get(d, 'show_to_ocpi_partners')} />} />
              <KVRow icon={<IoHomeSharp style={{ color: '#FF8B00' }} />} iconBg="rgba(255,139,0,0.1)" label="Home Charging" value={<BoolBadge value={_.get(d, 'does_support_home_charging')} trueText="Supported" falseText="No" />} />
              <KVRow icon={<IoWalletSharp style={{ color: '#be210b' }} />} iconBg="rgba(190,33,11,0.1)" label="ChargeCoin Disabled" value={<BoolBadge value={_.get(d, 'is_chargecoin_disabled')} trueText="Disabled" falseText="Enabled" />} />
              <KVRow icon={<IoBookmarkSharp style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Skip Booking" value={<BoolBadge value={_.get(d, 'skip_booking')} />} />
              <KVRow icon={<HiLightningBolt style={{ color: '#fbaa26' }} />} iconBg="rgba(251,170,38,0.1)" label="Power Limit Active" value={<BoolBadge value={_.get(d, 'station_power_limit_activate')} />} />
              <KVRow icon={<IoShieldCheckmarkSharp style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="SHWA Station" value={<BoolBadge value={_.get(d, 'is_shwa_station')} />} />
            </div>
          </div>

          {/* ── AMENITIES ── */}
          {!_.isEmpty(_.get(d, 'amenity', [])) && (
            <div className="sim-section">
              <div className="sim-section__title"><IoBookmarkSharp /> Amenities</div>
              <div className="sim-facilities-grid">
                {_.get(d, 'amenity', []).map((a, i) => (
                  <span className="sim-facility" key={i}>{_.get(a, 'name', a)}</span>
                ))}
              </div>
            </div>
          )}

          {/* ── TAGS ── */}
          {!_.isEmpty(_.get(d, 'tags', [])) && (
            <div className="sim-section">
              <div className="sim-section__title"><IoPricetagsSharp /> Tags</div>
              <div className="sim-tags-container">
                {_.get(d, 'tags', []).map((tag, i) => (
                  <span className="sim-tag" key={i}><IoPricetagsSharp style={{ fontSize: '0.6rem' }} />{_.get(tag, 'name', tag)}</span>
                ))}
              </div>
            </div>
          )}

          {/* ── BUSINESS & SAP ── */}
          <div className="sim-section">
            <div className="sim-section__title"><IoBusinessSharp /> Business & SAP Details</div>
            <div className="sim-kv-grid">
              <KVRow icon={<IoBusinessSharp style={{ color: '#5a9cf5' }} />} iconBg="rgba(60,124,221,0.1)" label="Business Place" value={_.get(d, 'businessPlace', 'N/A')} />
              <KVRow icon={<IoWalletSharp style={{ color: '#14ac7f' }} />} iconBg="rgba(20,172,127,0.1)" label="GLA Credit Account" value={_.get(d, 'glaCreditAccount', 'N/A')} />
              <KVRow icon={<HiIdentification style={{ color: '#fbaa26' }} />} iconBg="rgba(251,170,38,0.1)" label="WBS Element" value={_.get(d, 'wbsElement', 'N/A')} />
              <KVRow icon={<IoBusinessSharp style={{ color: '#be210b' }} />} iconBg="rgba(190,33,11,0.1)" label="Co-Capex Brand" value={renderSafeValue(_.get(d, 'co_capex_brand'))} />
            </div>
          </div>

          {/* ── EXTERNAL & FACILITIES ── */}
          {!_.isEmpty(_.get(d, 'external_location_facilities', [])) && (
            <div className="sim-section">
              <div className="sim-section__title"><IoGlobeOutline /> External Location Facilities</div>
              <div className="sim-facilities-grid">
                {_.get(d, 'external_location_facilities', []).map((f, i) => (
                  <span className="sim-facility" key={i}>{typeof f === 'string' ? f : _.get(f, 'name', JSON.stringify(f))}</span>
                ))}
              </div>
            </div>
          )}

          {/* ── EXTERNAL IMAGES ── */}
          {!_.isEmpty(_.get(d, 'external_location_images', [])) && (
            <div className="sim-section">
              <div className="sim-section__title"><IoImagesSharp /> External Location Images</div>
              <div className="sim-photos-strip">
                {_.get(d, 'external_location_images', []).map((img, i) => (
                  <img 
                    key={i} 
                    src={typeof img === 'string' ? img : _.get(img, 'url', '')} 
                    alt={`ext-img-${i}`} 
                    onClick={() => setZoomedImage(typeof img === 'string' ? img : _.get(img, 'url', ''))}
                    style={{ cursor: 'zoom-in' }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── SETTINGS ── */}
          {_.get(d, 'settings') && (
            <div className="sim-section">
              <div className="sim-section__title"><IoSettingsSharp /> Settings</div>
              <div className="sim-json-block">
                {typeof d.settings === 'object' ? JSON.stringify(d.settings, null, 2) : String(d.settings)}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

StationInfoModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  stationIcon: PropTypes.object,
};

export default StationInfoModal;
